import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  Users, TrendingUp, Zap, Globe, Copy, Check, ExternalLink
} from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDate } from '../../utils/formatDate';

import PageHeader from '../../components/PageHeader';
import { PageLoader } from '../../components/crm/ui';
const QUALITY_COLORS = { hot: '#BA0517', high: '#FF9900', medium: '#0176D3', low: '#706E6B' };

export default function CrmMarketingDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalLeads: 0,
    webformLeads: 0,
    averageScore: 0,
    highQualityCount: 0
  });
  const [leads, setLeads] = useState([]);
  const [formConfig, setFormConfig] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const [leadsRes, statsRes, configRes] = await Promise.all([
          api.get('/crm/leads'),
          api.get('/crm/leads/stats'),
          api.get('/crm/marketing/config')
        ]);

        if (leadsRes.data.success) {
          setLeads(leadsRes.data.leads || []);
        }

        if (statsRes.data.success) {
          // Calculate custom metrics from leads
          const allLeads = leadsRes.data.leads || [];
          const wfLeads = allLeads.filter(l => l.source === 'web_form').length;
          const avgScore = allLeads.length ? Math.round(allLeads.reduce((acc, l) => acc + (l.score || 0), 0) / allLeads.length) : 0;
          const hqCount = allLeads.filter(l => l.leadQuality === 'hot' || l.leadQuality === 'high').length;

          setStats({
            totalLeads: allLeads.length,
            webformLeads: wfLeads,
            averageScore: avgScore,
            highQualityCount: hqCount
          });
        }

        if (configRes.data.success) {
          setFormConfig(configRes.data.leadFormConfig);
        }
      } catch (err) {
        console.error('Error fetching marketing stats', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const formUrl = formConfig?.publicFormToken
    ? `${window.location.origin}/public-form.html?token=${formConfig.publicFormToken}`
    : '';
  const iframeCode = `<iframe src="${formUrl}" width="100%" height="550" style="border:none; border-radius:8px; box-shadow:0 4px 12px rgba(0,0,0,0.05);"></iframe>`;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(iframeCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    alertService.success('Copied', 'Embed code copied to clipboard!');
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  // Group leads by source
  const sourceCounts = leads.reduce((acc, lead) => {
    const src = lead.source || 'other';
    acc[src] = (acc[src] || 0) + 1;
    return acc;
  }, {});

  const sourceData = Object.keys(sourceCounts).map(name => ({
    name: name.replace('_', ' ').toUpperCase(),
    value: sourceCounts[name]
  }));

  // Lead Quality distribution
  const qualityCounts = leads.reduce((acc, lead) => {
    const q = lead.leadQuality || 'low';
    acc[q] = (acc[q] || 0) + 1;
    return acc;
  }, { hot: 0, high: 0, medium: 0, low: 0 });

  const qualityData = [
    { name: 'HOT (Score >= 75)', value: qualityCounts.hot, color: QUALITY_COLORS.hot },
    { name: 'HIGH (Score >= 50)', value: qualityCounts.high, color: QUALITY_COLORS.high },
    { name: 'MEDIUM (Score >= 25)', value: qualityCounts.medium, color: QUALITY_COLORS.medium },
    { name: 'LOW (Score < 25)', value: qualityCounts.low, color: QUALITY_COLORS.low },
  ];

  return (
    <div className="py-4 sm:py-6 px-0 sm:px-4 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader
        title="Lead Marketing Hub"
        subtitle="Measure form conversions, lead qualification scores, and auto-enrichment metrics."
        icon={TrendingUp}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Total CRM Leads</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{stats.totalLeads}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#2E844A]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Webform Submissions</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{stats.webformLeads}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#FF9900]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Avg Quality Score</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{stats.averageScore}/100</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#BA0517]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">High/Hot Leads</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{stats.highQualityCount}</h3>
        </div>
      </div>

      {/* Visual Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quality Distribution */}
        <div className="sf-card p-6">
          <h2 className="text-[13px] font-bold text-[#080707] mb-4">Lead Quality Distribution</h2>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={qualityData.filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {qualityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            {qualityData.map((q, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[11px]">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: q.color }} />
                <span className="text-[#706E6B] font-medium">{q.name}: </span>
                <span className="font-bold text-[#080707]">{q.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Lead Sources */}
        <div className="sf-card p-6">
          <h2 className="text-[13px] font-bold text-[#080707] mb-4">Lead Sources Breakdown</h2>
          {sourceData.length > 0 ? (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sourceData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#D8DDE6" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#706E6B' }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#706E6B' }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0176D3" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-[#706E6B] text-[13px]">
              No source telemetry available yet.
            </div>
          )}
        </div>
      </div>

      {/* Embed form section */}
      <div className="sf-card p-8 bg-[#080707] text-white flex flex-col md:flex-row items-center justify-between gap-6 border-none">
        <div className="space-y-3 max-w-xl">
          <div className="inline-flex items-center gap-2 bg-[#0176D3]/15 text-[#5DB4F5] px-3 py-1 rounded-full text-[11px] font-bold">
            <Globe size={14} /> Web-to-Lead Forms
          </div>
          <h2 className="text-xl font-bold">Embeddable Capture Forms</h2>
          <p className="text-[#B0AEAB] text-[13px] leading-relaxed">
            Generate customized forms that embed on your website. Every visitor submission is auto-enriched with company profiles, scored for quality, and injected straight into your CRM leads list.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
          <button
            onClick={copyEmbedCode}
            disabled={!formUrl}
            className="h-9 px-5 bg-white/10 hover:bg-white/20 rounded text-[13px] font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-40 whitespace-nowrap shrink-0"
          >
            {copied ? <Check size={16} className="text-[#4BC076]" /> : <Copy size={16} />}
            Copy Embed Code
          </button>
          <a
            href={formUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!formUrl}
            className={`h-10 px-5 bg-[#0176D3] hover:bg-[#014486] rounded text-[13px] font-bold transition-all flex items-center justify-center gap-2 ${!formUrl ? 'pointer-events-none opacity-40' : ''}`}
          >
            Preview Web Form <ExternalLink size={16} />
          </a>
        </div>
      </div>

      {/* Recent submissions timeline */}
      <div className="sf-table-container">
        <div className="px-5 py-4 border-b border-[#D8DDE6] flex justify-between items-center">
          <h2 className="font-bold text-[#080707] text-[14px]">Latest Form Leads</h2>
          <span className="text-[11px] text-[#706E6B] font-bold uppercase tracking-wider">Auto-scored & enriched</span>
        </div>
        <div className="overflow-x-auto">
          <table className="sf-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Source</th>
                <th>Quality Tier</th>
                <th>Calculated Score</th>
                <th className="text-right">Captured At</th>
              </tr>
            </thead>
            <tbody>
              {leads.filter(l => l.source === 'web_form').slice(0, 5).map((l, idx) => (
                <tr key={idx}>
                  <td className="font-bold text-[#080707]">{l.name}</td>
                  <td className="text-[#706E6B]">{l.email || 'N/A'}</td>
                  <td><span className="sf-badge sf-badge-success">Web Form</span></td>
                  <td>
                    <span className={`sf-badge ${
                      l.leadQuality === 'hot' ? 'sf-badge-error' :
                      l.leadQuality === 'high' ? 'sf-badge-warning' :
                      l.leadQuality === 'medium' ? 'sf-badge-manager' :
                      'sf-badge-neutral'
                    }`}>
                      {l.leadQuality}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[#080707] tabular-nums">{l.score || 0}</span>
                      <div className="w-16 h-1.5 bg-[#F3F2F2] border border-[#D8DDE6] rounded-full overflow-hidden">
                        <div className="h-full bg-[#0176D3]" style={{ width: `${l.score || 0}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="text-right text-[#706E6B]">{formatDate(l.createdAt)}</td>
                </tr>
              ))}
              {leads.filter(l => l.source === 'web_form').length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#706E6B]">
                    No web form leads submitted yet. Click "Preview Web Form" to test!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
